<script setup lang="ts">
import { computed } from 'vue'

type FinestButtonVariant = 'primary' | 'gradient' | 'secondary' | 'ghost' | 'warning' | 'danger'
type FinestButtonSize = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: FinestButtonVariant
    size?: FinestButtonSize
    label?: string
    icon?: string
    iconPos?: 'left' | 'right'
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    blocked?: boolean
  }>(),
  {
    variant: 'primary',
    size: 'md',
    iconPos: 'left',
    type: 'button',
    disabled: false,
    blocked: false,
  },
)

const variantClasses: Record<FinestButtonVariant, string> = {
  primary: 'bg-action text-white hover:bg-action-hover',
  gradient: 'text-white border-0 bg-[image:var(--gradient-button)] bg-[length:140%_100%] hover:bg-[position:100%_0]',
  secondary: 'bg-surface text-ink border-line hover:border-ink-3 hover:bg-surface-2',
  ghost: 'bg-transparent text-action hover:bg-info-subtle',
  warning: 'bg-warning-subtle text-warning border-warning-subtle-active hover:bg-warning-subtle-hover',
  danger: 'bg-danger text-white hover:bg-danger-hover',
}

const sizeClasses: Record<FinestButtonSize, string> = {
  sm: 'h-7 px-[11px] text-xs',
  md: 'h-[34px] px-4 text-base',
  lg: 'h-[42px] px-[22px] text-lg',
}

const classes = computed(() => [
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-transparent font-medium leading-none transition-all focus-visible:ring-[3px] focus-visible:ring-action-700/20 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  variantClasses[props.variant],
  sizeClasses[props.size],
  props.blocked ? 'cursor-not-allowed opacity-50' : '',
])
</script>

<template>
  <button :type="type" :class="classes" :disabled="disabled" :aria-disabled="blocked || undefined">
    <i v-if="icon && iconPos === 'left'" :class="icon" aria-hidden="true" />
    <slot>{{ label }}</slot>
    <i v-if="icon && iconPos === 'right'" :class="icon" aria-hidden="true" />
  </button>
</template>
